import { LngLatLike, LngLat } from 'maplibre-gl'
import config from '@src/config'
import axios from 'axios'
import { AxiosRequestConfig } from 'axios'
import { TravelMode, AvoidType, ApproachType } from '@src/common/interface'
import { NBRequestStatus } from './common'

export interface DistanceMatrixRequest {
  /** Origins are the starting point of your route. Ensure that origins are a valid land location to calculate a route. Multiple origins are separated by a pipe symbol. */
  origins: LngLatLike[]
  /** Destinations are the ending coordinates of your route. Ensure that destinations are at a valid land location. Multiple destinations are separated by a pipe symbol.  */
  destinations: LngLatLike[]
  /** Sets the time of departure. The response will return a route based on traffic for that specific departure time. */
  departureTime?: number
  /** A parameter which which sets the transportation mode for the route. PS: Only the 4w/car profile is enabled by default. The other customised routing profiles are only supported in premium plan. */
  mode?: TravelMode | string
  /** Setting this will ensure the route avoids ferry, tolls or highways. */
  avoid?: AvoidType
  /** A list indicating the side of the road from which to approach waypoints in a requested route. If provided, the number of approaches must be equal to the number of all points on the path. */
  approaches?: ApproachType[]
  /**
   * api key for the request
   */
  apiKey?: string
}

interface NormalizedDistanceMatrixRequest extends DistanceMatrixRequest {
  /** The starting point of a route. */
  origins: LngLat[]
  /** The ending point of a route. */
  destinations: LngLat[]
}

export interface DistanceMatrixResult {
  /** A string indicating the state of the response. This is a separate code than the HTTP status code. On normal valid responses, the value will be `Ok`. */
  status: NBRequestStatus
  /** Container object for response. Array of elements. Each row corresponds to one Origin.Each element object corresponds to a destination. */
  rows: Array<DistanceMatrixResponseRow>
  /** error message if status is not 200. */
  msg?: string
}

export interface DistanceMatrixResponseRow {
  /** elements for a particular row|origin */
  elements: Array<DistanceMatrixResponseElement>
}

export interface DistanceMatrixResponseElement {
  /** traveling distance between origin and destination. Unit: seconds */
  distance: {
    value: number
  }
  /** traveling duration between origin and destination. Unit: seconds */
  duration: {
    value: number
  }
}

function checkAndnormalizeOpt(opt: DistanceMatrixRequest): NormalizedDistanceMatrixRequest {
  if (!Array.isArray(opt.origins) || opt.origins.length === 0) {
    throw new Error('origin is required in DistanceMatrixRequest')
  }

  if (!Array.isArray(opt.destinations) || opt.destinations.length === 0) {
    throw new Error('destination is required in DistanceMatrixRequest')
  }

  const nOpt: NormalizedDistanceMatrixRequest = Object.assign({}, opt, {
    origin: opt.origins.map((origin) => LngLat.convert(origin)),
    destination: opt.destinations.map((destination) => LngLat.convert(destination)),
  }) as NormalizedDistanceMatrixRequest

  return nOpt
}

export class DistanceMatrixService {
  async getDistanceMatrix(opt: DistanceMatrixRequest): Promise<DistanceMatrixResult> {
    const nOpt = checkAndnormalizeOpt(opt)

    if (!config.API_KEY && !opt.apiKey) {
      throw new Error('In order to call the NextBillion.ai API, you must provide a valid API Key.')
    }

    if (!config.API_HOST) {
      throw new Error('An API host is required to make request to NextBillion.ai API.')
    }

    const params: any = {
      origins: nOpt.origins.map((origin) => `${origin.lat},${origin.lng}`).join('|'),
      destinations: nOpt.destinations.map((dest) => `${dest.lat},${dest.lng}`).join('|'),
    }

    if (!nOpt.departureTime || (typeof nOpt.departureTime === 'string' && isNaN(parseInt(nOpt.departureTime)))) {
      nOpt.departureTime = 0
    }
    params.departure_time = nOpt.departureTime

    if (nOpt.avoid) {
      params.avoid = nOpt.avoid
    }

    if (nOpt.mode) {
      params.mode = nOpt.mode
    }

    if (nOpt.approaches) {
      params.approaches = nOpt.approaches.join(';')
    }

    const reqConfig: AxiosRequestConfig = {
      params,
    }
    if (config.API_KEY_HEADER_NAME) {
      reqConfig.headers = {
        [config.API_KEY_HEADER_NAME]: nOpt.apiKey || config.API_KEY,
      }
    } else {
      params.key = nOpt.apiKey || config.API_KEY
    }

    return axios
      .get(`https://${config.API_HOST}/distancematrix/json`, reqConfig)
      .then((resp) => resp.data as DistanceMatrixResult)
      .catch((err) => {
        if (err.response && err.response.data) {
          return err.response.data as DistanceMatrixResult
        } else {
          return err
        }
      })
  }
}
